(ns practitest.frontend.common.re-frame
  (:require
   [ajax.core :as ajax]
   [clojure.string :as str]
   [clojure.walk :as walk]
   [malli.core :as m]
   [malli.error :as me]
   [malli.transform :as mt]
   [practitest.frontend.common.fetch :refer [fetch-json]]
   [practitest.frontend.routing.backend-routes :as broutes]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]))

(defn build-event-vector
  "Small helper to dynamically build event vectors for re-frame.

  Will accept `event` in form of either `:keyword` or `[:keyword arg]` 
  and will build resulting event vector by concatenating args to base event.

  e.g. `(build-event-vector :keyword 1 2 3) => [:keyword 1 2 3]` and
       `(build-event-vector [:keyword 1] 2 3) => [:keyword 1 2 3]`

  Used in dynamic and conditional dispatch functions"
  [event & args]
  (if (coll? event)
    (apply conj event args)
    (apply conj [event] args)))

;; Stable DOM handlers
(defn dispatch2
  [event & params]
  (re-frame/dispatch (apply build-event-vector event params)))

(defn partial-dispatch
  [event]
  (reagent/partial dispatch2 event))

(defn dispatch2-sync
  [event & params]
  (re-frame/dispatch-sync (apply build-event-vector event params)))

(defn partial-dispatch-sync
  [event]
  (reagent/partial dispatch2-sync event))

(defn subscribe2
  [query & params]
  (re-frame/subscribe (apply build-event-vector query params)))

(defn partial-subscribe
  [event]
  (reagent/partial subscribe2 event))

(defn conditional-dispatch
  "Will try to append event with args to effects map inside `:fx` section using `:dispatch` effect.

  * Will preserve existing `:fx` items
  * Will return effects-map unchanged if event is `nil`"
  [effects-map event & args]
  (if (some? event)
    (update effects-map :fx
            (fn [fx]
              (let [event-vector (apply build-event-vector event args)]
                (if (coll? fx)
                  (conj fx [:dispatch event-vector])
                  [[:dispatch event-vector]]))))
    effects-map))

(comment
  (conditional-dispatch {} [:foo 1] 2 3)
  (conditional-dispatch {:dispatch []} [:foo 1] 2 3)
  (conditional-dispatch {:fx [[:dispatch [:asd]]]} :foo 2 3)
  (conditional-dispatch {:fx [[:dispatch [:asd]]]} nil 2 3))

(defn underscore->dash [m]
  (walk/postwalk
   (fn [x]
     (if (map? x)
       (update-keys x #(-> %
                           name
                           (str/replace "_" "-")
                           keyword))
       x))
   m))

(defn reg-load-data-event
  "Registers three re-frame events from a single config map.

   Params:
   :evt       — keyword, event id (also used to derive -success/-failed ids)
   :db-path   — vector path in app-db to store the result
   :schema    — malli schema, validates the transform output
   :transform — fn [evt data], transforms response before storing (default: identity)
   :merge-by  — keyword, to get merge-key from recived data

   Registered events:
   event-fx:
     evt — dispatched as [evt id-or-url]

   event-db:
     evt-success — converts underscored keys to dashed, runs :transform,
                   validates and coercing result against :schema, merges into db
                   db scoped by :db-path,
                   if :merge-by or id(from [evt id]) presence use them to merge
     evt-failed  — logs error, leaves db unchanged"
  [{:keys [evt db-path merge-by schema transform]
    :or   {transform identity
           schema    :any}}]
  (let [ns              (namespace evt)
        success-evt     (keyword ns (str (name evt) "-success"))
        failed-evt      (keyword ns (str (name evt) "-failed"))
        mschema         (m/schema schema)
        coerce-data     (m/coercer mschema (mt/transformer mt/string-transformer mt/default-value-transformer))
        success-handler (fn [db [_ id-or-url response]]
                          (let [id        (when (number? id-or-url) id-or-url)
                                raw       (->> response underscore->dash (transform evt))
                                data      (try
                                            (coerce-data raw)
                                            (catch :default e
                                              (js/console.error "Coercion failed for" evt (ex-message e))
                                              raw))
                                merge-key (or (some->> merge-by (get data))
                                              id)]
                            (if merge-key
                              (update db merge-key merge data)
                              (merge db data))))
        failed-handler  (fn [db [_evt _id-or-url response]]
                          (when ^boolean goog.DEBUG
                            (js/console.error "Load data: failed" evt response))
                          db)
        scoped          [(re-frame/path db-path)]]
    (re-frame/reg-event-fx
     evt
     scoped
     (fn [{db :db} [_ id-or-url]]
       (let [url (cond-> id-or-url
                   (number? id-or-url)
                   (->> (get db) (broutes/get-url2 evt)))]
         {:http-xhrio {:method          :get
                       :format          (ajax/json-request-format)
                       :response-format (ajax/json-response-format)
                       :uri             url
                       :on-success      [success-evt id-or-url]
                       :on-failure      [failed-evt id-or-url]}})))

    (re-frame/reg-event-db
     success-evt
     scoped
     success-handler)

    (re-frame/reg-event-db
     failed-evt
     failed-handler)))

(defn reg-load-data-events [& events]
  (doseq [evt events]
    (reg-load-data-event evt)))

;; ---- effects ----

;; NOTE: this is used instead of legacy :http-xhrio (which uses XHR)
(re-frame/reg-fx
  ::http
  (fn [{:keys [url method body on-success on-failure] :or {method "GET"}}]
    (-> (fetch-json url (cond-> {:method method}
                          body (assoc :body body)))
        (.then (fn [{:keys [ok? body]}]
                 (cond
                   (and ok? on-success) (re-frame/dispatch (conj on-success body))
                   (and (not ok?) on-failure) (re-frame/dispatch (conj on-failure body))
                   (not ok?) (js/console.warn "HTTP failed:" url body))))
        (.catch (fn [err]
                  (if on-failure
                    (re-frame/dispatch (conj on-failure {:error (.-message err)}))
                    (js/console.warn "HTTP error:" url err)))))))

;; ---- interceptors ----

(defn mk-state-validator [state-path schema]
  (re-frame/after
    (fn [db _event]
      (when-let [state (get-in db state-path)]
        (when-not (m/validate schema state)
          (js/console.error
            (str "Invalid state at " state-path " after event:")
            (clj->js (me/humanize (m/explain schema state)))))))))
